/**
 * useStaff — ViewModel for /iam/staff (list + create + update).
 *
 * Permissions live server-side (Layer 2c). The hook always tries the
 * fetch; the API returns 403 if the current user isn't allowed, and
 * the page surfaces that as an inline error.
 */

import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '../context/AuthContext'
import { APIROUTES, fillPath } from '../constants/routes.constants'
import type {
  CreateStaffInput,
  StaffDto,
  UpdateStaffInput,
  UserTypeOption
} from '../models/staff.model'
import { mutateJson } from '../utils/api/mutateJson'
import { parseApiError } from '../utils/api/parseApiError'
import { usePaginatedList } from './usePaginatedList'

// Single-source-of-truth: src/models/staff.model.ts.
// Per SOP 02 §"Mutation Patterns (LOCKED)" — DTO source rule.
export type {
  CreateStaffInput,
  StaffDto,
  UpdateStaffInput,
  UserTypeOption
} from '../models/staff.model'

interface ListBody {
  readonly success: true
  readonly data: {
    readonly items: StaffDto[]
    readonly total: number
    readonly page: number
    readonly pageSize: number
  }
}

interface UserTypesListBody {
  readonly success: true
  readonly data: {
    readonly items: ReadonlyArray<{ code: string; name: string; is_active: 0 | 1 }>
  }
}

export interface UseStaffReturn {
  readonly items: StaffDto[]
  readonly total: number
  readonly page: number
  readonly pageSize: number
  readonly isLoading: boolean
  readonly error: string | null
  readonly setPage: (n: number) => void
  readonly reload: () => void
  /** User-type options for the create form (admin + customer filtered out server-side via STAFFFORBIDDENUSERTYPES). */
  readonly userTypeOptions: UserTypeOption[]
  readonly create: (
    input: CreateStaffInput
  ) => Promise<
    { ok: true; data: StaffDto }
    | { ok: false; field: string | null; message: string }
  >
  readonly update: (
    id: string,
    patch: UpdateStaffInput
  ) => Promise<
    { ok: true; data: StaffDto }
    | { ok: false; field: string | null; message: string }
  >
  readonly remove: (
    id: string
  ) => Promise<{ ok: true } | { ok: false; message: string }>
}

/** User-type codes the staff form deliberately hides — see
 *  STAFFFORBIDDENUSERTYPES in src/constants/staff.constants.ts. */
const FORBIDDEN_USER_TYPES: ReadonlySet<string> = new Set(['admin', 'customer'])

export function useStaff(): UseStaffReturn {
  const { authedFetch } = useAuth()
  const [userTypeOptions, setUserTypeOptions] = useState<UserTypeOption[]>([])

  const paged = usePaginatedList<StaffDto, undefined>({
    fetcher: useCallback(
      async (page, pageSize, signal) => {
        const url = `${APIROUTES.IAMSTAFF}?page=${page}&pageSize=${pageSize}`
        const res = await authedFetch(url, { signal })
        if (!res.ok) {
          const { message } = await parseApiError(res)
          throw new Error(message)
        }
        const body = (await res.json()) as ListBody
        return { items: body.data.items, total: body.data.total }
      },
      [authedFetch]
    )
  })

  // Load eligible user types once on mount. Single read of
  // /iam/user-types with a generous pageSize covers the realistic
  // 5-10 user_types in the system.
  useEffect(() => {
    const ctrl = new AbortController()
    let cancelled = false
    void (async () => {
      try {
        const res = await authedFetch(`${APIROUTES.IAMUSERTYPES}?page=1&pageSize=100`, {
          signal: ctrl.signal
        })
        if (!res.ok) return
        const body = (await res.json()) as UserTypesListBody
        const filtered = body.data.items
          .filter((row) => row.is_active === 1 && !FORBIDDEN_USER_TYPES.has(row.code))
          .map((row) => ({ code: row.code, name: row.name }))
        if (!cancelled) setUserTypeOptions(filtered)
      } catch {
        // best-effort — the form falls back to an empty select; the
        // user sees the message and admin can investigate.
      }
    })()
    return () => {
      cancelled = true
      ctrl.abort()
    }
  }, [authedFetch])

  const create = useCallback(
    async (input: CreateStaffInput) => {
      const r = await mutateJson<StaffDto>(
        authedFetch,
        APIROUTES.IAMSTAFF,
        'POST',
        input
      )
      if (!r.ok) return r
      if (paged.page === 1) paged.insertItem(r.data)
      else paged.reload()
      return { ok: true as const, data: r.data }
    },
    [authedFetch, paged]
  )

  const update = useCallback(
    async (id: string, patch: UpdateStaffInput) => {
      const r = await mutateJson<StaffDto>(
        authedFetch,
        fillPath(APIROUTES.IAMSTAFFITEM, { id }),
        'PATCH',
        patch
      )
      if (!r.ok) return r
      paged.updateItem((it) => it.id === id, r.data)
      return { ok: true as const, data: r.data }
    },
    [authedFetch, paged]
  )

  const remove = useCallback(
    async (id: string) => {
      const r = await mutateJson<{ ok: true }>(
        authedFetch,
        fillPath(APIROUTES.IAMSTAFFITEM, { id }),
        'DELETE'
      )
      if (!r.ok) return { ok: false as const, message: r.message }
      paged.removeItem((it) => it.id === id)
      return { ok: true as const }
    },
    [authedFetch, paged]
  )

  return {
    items: paged.items,
    total: paged.total,
    page: paged.page,
    pageSize: paged.pageSize,
    isLoading: paged.isLoading,
    error: paged.error,
    setPage: paged.setPage,
    reload: paged.reload,
    userTypeOptions,
    create,
    update,
    remove
  }
}

/** Lookup helper for the edit page. */
export function findStaffById(items: ReadonlyArray<StaffDto>, id: string): StaffDto | null {
  return items.find((s) => s.id === id) ?? null
}
